import { StorageManager, Guess, Game, QUESTION_VALUE, TRIVIA_VALUE, GUESS_VALUE }  from './Common';
import { API_CONFIG } from '../configuration/api';

class RemoteManager implements StorageManager {
    private game: Game;
    private apiURL: string;

    constructor() {
        this.game = {
            firstname: '',
            lastname: '',
            gameid: '',
            gamedate: '',
            questions: {
                standard: [],
                unique: []
            },
            trivia: [],
            guesses: [],
            asks: [],
            triviaAsks: [],
            score: 0,
            gameCompleted: false
        }

        this.apiURL = API_CONFIG.getAuthorizedBaseUrl();
    }

    async loadGame(token?: string): Promise<Game> {
        if (!token) {
            console.error('Token is required for remote game loading');
            return this.game;
        }


        try {
            const response = await fetch(API_CONFIG.getGamesUrl(true), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });


            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Failed to load remote game: ${response.status} ${response.statusText}`);
                console.error('Error response body:', errorText);
                if (response.status === 401 || response.status === 403) {
                    console.error('Authentication failed - token may be invalid or expired');
                }
                return this.game;
            }

            const data = await response.json();
            
            // Ensure score is set to 0 if not provided by the endpoint
            this.game = {
                ...data,
                score: data.score || 0,
                triviaAsks: data.triviaAsks || [],
                trivia: data.trivia || [],
                gameCompleted: data.gameCompleted || false
            };
        } catch (error) {
            console.error('❌ ERROR: Failed to load remote game!', error);
            // Return default game on error
        }
        
        
        return this.game;
    }

    async recordQuestion(id: number, token?: string): Promise<number> {
        if (!token) {
            console.error('Token is required for remote question recording');
            return this.game.score;
        }

        try {
            const response = await fetch(API_CONFIG.buildUrl('/game-events', true), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    eventType: 'question',
                    eventData: { questionId: id }
                })
            });

            if (!response.ok) {
                console.error(`Failed to record question: ${response.status} ${response.statusText}`);
                if (response.status === 401 || response.status === 403) {
                    console.error('Authentication failed for question recording');
                }
                return this.game.score;
            }

            const data = await response.json();

            if (data.success) {
                // Update local state with server response
                this.game.asks.push(id);
                this.game.score = data.newScore;
            }
        } catch (error) {
            console.error('Error recording question:', error);
            
            // Check if it's a CORS error
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                console.warn('CORS error detected - likely backend needs to allow localhost:3000 origin');
                console.warn('Falling back to local score update only');
                
                // Ensure game state is properly initialized before updating
                if (!this.game.asks) {
                    console.warn('Initializing missing asks array');
                    this.game.asks = [];
                }
                
                // Update local state even though remote failed
                this.game.asks.push(id);
                this.game.score = (this.game.score || 0) + QUESTION_VALUE;
            }
        }
        return this.game.score;
    }

    async recordTrivia(id: number, token?: string): Promise<number> {
        if (!token) {
            console.error('Token is required for remote trivia recording');
            return this.game.score;
        }

        try {
            const response = await fetch(API_CONFIG.buildUrl('/game-events', true), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    eventType: 'trivia',
                    eventData: { triviaId: id }
                })
            });

            if (!response.ok) {
                console.error(`Failed to record trivia: ${response.status} ${response.statusText}`);
                if (response.status === 401 || response.status === 403) {
                    console.error('Authentication failed for trivia recording');
                }
                return this.game.score;
            }

            const data = await response.json();

            if (data.success) {
                // Update local state with server response
                this.game.triviaAsks.push(id);
                this.game.score = data.newScore;
            }
        } catch (error) {
            console.error('Error recording trivia:', error);
            
            // Check if it's a CORS error
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                console.warn('CORS error detected for trivia - likely backend needs to allow localhost:3000 origin');
                console.warn('Falling back to local score update only');
                
                // Ensure game state is properly initialized before updating
                if (!this.game.triviaAsks) {
                    console.warn('Initializing missing triviaAsks array');
                    this.game.triviaAsks = [];
                }
                
                // Update local state even though remote failed
                this.game.triviaAsks.push(id);
                this.game.score = (this.game.score || 0) + TRIVIA_VALUE;
            }
        }
        return this.game.score;
    }

    async recordGuess(guess: Guess, token?: string): Promise<number> {
        if (!token) {
            console.error('Token is required for remote guess recording');
            return this.game.score;
        }

        try {
            const response = await fetch(API_CONFIG.buildUrl('/game-events', true), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    eventType: 'guess',
                    eventData: { 
                        guess: {
                            firstname: guess.firstname,
                            middlename: guess.middlename,
                            lastname: guess.lastname
                        }
                    }
                })
            });

            if (!response.ok) {
                console.error(`Failed to record guess: ${response.status} ${response.statusText}`);
                if (response.status === 401 || response.status === 403) {
                    console.error('Authentication failed for guess recording');
                }
                return this.game.score;
            }

            const data = await response.json();
            if (data.success) {
                // Update local state with server response
                this.game.guesses.push(guess);
                this.game.score = data.newScore;
                this.game.gameCompleted = data.gameCompleted || false;
            }
        } catch (error) {
            console.error('Error recording guess:', error);
            
            // Check if it's a CORS error
            if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
                console.warn('CORS error detected for guess - likely backend needs to allow localhost:3000 origin');
                console.warn('Falling back to local score update only');
                
                // Ensure game state is properly initialized before updating
                if (!this.game.guesses) {
                    console.warn('Initializing missing guesses array');
                    this.game.guesses = [];
                }
                
                // Update local state even though remote failed
                this.game.guesses.push(guess);
                this.game.score = (this.game.score || 0) + GUESS_VALUE;
            }
        }
        return this.game.score;
    }

    // Method to update game completion status (for frontend to call when guess is correct)
    updateGameCompletion(completed: boolean): void {
        this.game.gameCompleted = completed;
        // For RemoteManager, the backend will handle persistence via the API call
    }

    // Force reload from server - use sparingly to minimize traffic
    async forceReload(token?: string): Promise<Game> {
        const reloadedGame = await this.loadGame(token);
        return reloadedGame;
    }
}

export default RemoteManager
